import { useEffect, useMemo, useState } from 'react';
import { useFeatureGate } from '@atlassian/feature-gates-react';
import { OAuthConsentFeatureGate } from '../../../FeatureGates/FeatureGates';
import { useInfo } from '../../../Flow/ConsentContextProvider';
import type { BitbucketWorkspace } from '#definitions/consent-info';

export interface BitbucketWorkspaceSelection {
  /**
   * Whether the Bitbucket selector should render (gate on + /consent/info
   * returned at least one workspace in `availableBitbucketWorkspaces`). False
   * when the key is absent or the list is empty — the selector is hidden.
   */
  isBitbucketWorkspaceAccessEnabled: boolean;
  /** Bitbucket workspaces available for selection. Empty when the gate is off. */
  availableBitbucketWorkspaces: BitbucketWorkspace[];
  /** Currently selected Bitbucket workspaces (multi-select, unbounded). */
  selectedBitbucketWorkspaces: BitbucketWorkspace[];
  setSelectedBitbucketWorkspaces: (workspaces: BitbucketWorkspace[]) => void;
}

/**
 * Bitbucket workspace selection for the MCP consent flow.
 *
 * Bitbucket workspaces are returned directly by /consent/info and only exposed
 * when the `oauth-consent_bitbucket-mcp-workspace` gate is on. The selector
 * renders only when the gate is on and the response includes at least one
 * workspace — an absent or empty `availableBitbucketWorkspaces` hides it
 * entirely. Selection is multi-select; each selected ARI is submitted as an
 * extra `selectedWorkspace` on Accept, and `useBitbucketPolicy` runs a
 * /consent/workspace policy check per selected ARI so the returned toolsets
 * join the displayed policy.
 *
 * Kept as a standalone hook so McpContextProvider can fold the result into its
 * context value without carrying the gate/state wiring inline.
 */
export function useBitbucketWorkspaces(): BitbucketWorkspaceSelection {
  const info = useInfo();
  const gateEnabled = useFeatureGate(
    OAuthConsentFeatureGate.BITBUCKET_MCP_WORKSPACE,
  );

  const availableBitbucketWorkspaces = useMemo<BitbucketWorkspace[]>(
    () => (gateEnabled ? (info.availableBitbucketWorkspaces ?? []) : []),
    [gateEnabled, info.availableBitbucketWorkspaces],
  );

  // Render the selector only when there is at least one workspace to pick. The
  // gate is already folded in above (off → empty list), so a gate-off, missing
  // key, or empty array all hide the field.
  const isBitbucketWorkspaceAccessEnabled =
    availableBitbucketWorkspaces.length > 0;

  const [selectedBitbucketWorkspaces, setSelectedBitbucketWorkspaces] =
    useState<BitbucketWorkspace[]>([]);

  // Drop any selections that are no longer among the available workspaces
  // (e.g. the gate turned off or /consent/info changed).
  useEffect(() => {
    setSelectedBitbucketWorkspaces((current) => {
      const availableAris = new Set(
        availableBitbucketWorkspaces.map((ws) => ws.ari),
      );
      const next = current.filter((ws) => availableAris.has(ws.ari));
      return next.length === current.length ? current : next;
    });
  }, [availableBitbucketWorkspaces]);

  return {
    isBitbucketWorkspaceAccessEnabled,
    availableBitbucketWorkspaces,
    selectedBitbucketWorkspaces,
    setSelectedBitbucketWorkspaces,
  };
}
